import React, { useState, useEffect } from 'react'
import { TextArea } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import i18n from 'i18next'
import { MAX_LENGTH, LATIN_WITH_NUMBERS_SYMBOLS_EXTENDED } from '../../../constants/constants'
import { map, isNil } from 'ramda'
import { getUserValue } from '../utils'

/**
 * @param {string} i18nLabel
 * @param {string} name
 * @param {?number} maxlength
 * @param {?string} additionalInfo
 * @param {boolean} required
 * @param {boolean} allowNonLatin
 */
const ExternalTextArea = ({ i18nLabel, name, maxlength, additionalInfo, required, allowNonLatin }) => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [label, setLabel] = useState('')
  const [isValid, setIsValid] = useState(null)
  const [isFilled, setIsFilled] = useState(false)
  const [error, setError] = useState('')
  const [i18nByLanguage, setI18nByLanguage] = useState({})
  const [{ user, form: { accId } }, dispatch] = useStore()
  const userValue = getUserValue(accId, user, name)

  const presetValue = (value) => {
    setValue(value)
    const isValid = getIsValid(value)
    setIsValid(isValid)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  useEffect(() => {
    if (!isNil(userValue) && userValue !== '') {
      presetValue(userValue)
    }
  }, [userValue, i18nByLanguage, name])

  useEffect(() => {
    try {
      const labelObjectByLanguage = JSON.parse(i18nLabel)
      if (typeof labelObjectByLanguage === 'object') {
        setI18nByLanguage(labelObjectByLanguage)
      }
    } catch {
      setI18nByLanguage({})
    }
  }, [i18nLabel])

  useEffect(() => {
    const label = i18nByLanguage[i18n.language] || i18nByLanguage.en
    setLabel(label)
  }, [i18nByLanguage, i18n.language])

  const onChange = ({ target: { value } }) => {
    setValue(value)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
  }

  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = value.trim()
    const isValid = getIsValid(trimmedValue)

    if (isValid) {
      setIsValid(true)
      setError('')
      dispatch({ type: 'setExtFieldValue', name, value })
    } else {
      setIsValid(false)
      if (required || (!required && value.length)) {
        setError(!isFilled ? '' : 'generatedField.onlyLatin')
      } else {
        setError('')
      }
      dispatch({ type: 'removeFieldValue', name })
    }

    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  const getIsValid = (value) => allowNonLatin ? !!value.length : LATIN_WITH_NUMBERS_SYMBOLS_EXTENDED.test(value)

  return (
    <TextArea
      name={name}
      label={label}
      value={value}
      key={name}
      maxLength={maxlength || MAX_LENGTH}
      error={t(error)}
      onChange={onChange}
      onBlur={onBlur}
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
    />
  )
}

const getExternalTextAreaFields = map(textAreaField => {
  const { i18n, name, required, order, maxLength, additionalInfo, allowNonLatin } = textAreaField
  return (
    <ExternalTextArea
      i18nLabel={i18n}
      name={name}
      required={required}
      key={name}
      order={order}
      additionalInfo={additionalInfo}
      maxlength={maxLength}
      allowNonLatin={allowNonLatin}
    />
  )
})

export { ExternalTextArea, getExternalTextAreaFields }
